import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Card,
  Table,
  Button,
  Form,
  InputGroup,
} from 'react-bootstrap';
import { FaSearch, FaEye } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '../styles/DeviceTable.css';

import {
  fetchDevicesForAdmin,
  fetchDevicesForClientAdmin,
  fetchDevicesForSubadmin,
  fetchDevicesForSupervisor,
} from '../../redux/deviceActions';

const ROWS_PER_PAGE_OPTIONS = [10, 20, 50, 100];

const ManageDevice = () => {
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1); // 1-based
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [userType, setUserType] = useState(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  // Server-side pagination metadata comes straight from the store now.
  const { devices, loading, error, totalCount, totalPages } = useSelector(
    (state) => state.device,
  );

  /* ------------------------------------------------------------------
   *  RESOLVE ROLE ONCE
   * ---------------------------------------------------------------- */
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      setUserType(jwtDecode(token).userType);
    } catch (_) {
      toast.error('Invalid token. Please login again.');
    }
  }, []);

  /* ------------------------------------------------------------------
   *  DEBOUNCE SEARCH (400ms) — reset to page 1 on a new query
   * ---------------------------------------------------------------- */
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  /* ------------------------------------------------------------------
   *  FETCH THE CURRENT PAGE FROM THE SERVER
   * ---------------------------------------------------------------- */
  const fetchForRole = useCallback(
    (params) => {
      switch (userType) {
        case 'Admin':
          return fetchDevicesForAdmin(params);
        case 'Subadmin':
          return fetchDevicesForSubadmin(params);
        case 'ClientAdmin':
          return fetchDevicesForClientAdmin(params);
        case 'Supervisor':
          return fetchDevicesForSupervisor(params);
        default:
          return null;
      }
    },
    [userType],
  );

  useEffect(() => {
    if (!userType) return;
    const thunk = fetchForRole({
      page: currentPage,
      limit: rowsPerPage,
      search: debouncedSearch,
    });
    if (thunk) dispatch(thunk);
  }, [dispatch, fetchForRole, userType, currentPage, rowsPerPage, debouncedSearch]);

  const safeTotalPages = Math.max(1, totalPages || 1);
  const rangeStart = totalCount === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const rangeEnd = Math.min(currentPage * rowsPerPage, totalCount);

  /* ------------------------------------------------------------------
   *  RENDER
   * ---------------------------------------------------------------- */
  return (
    <Container fluid className="py-4">
      <Card className="shadow-lg border-0 rounded-4">
        {/* ---------- HEADER ---------- */}
        <Card.Header className="bg-white border-bottom-0 pt-4 pb-3">
          <h4 className="fw-bold text-primary mb-1">Manage Devices</h4>
          <small className="text-muted">
            Search, monitor and open the control panel for each sensor
          </small>
        </Card.Header>

        <Card.Body className="p-4">
          {/* ---------- SEARCH + PAGE SIZE ---------- */}
          <div className="d-flex flex-wrap gap-3 align-items-center mb-4">
            <InputGroup size="lg" className="shadow-sm flex-grow-1" style={{ minWidth: 240 }}>
              <InputGroup.Text className="bg-white border-end-0">
                <FaSearch className="text-primary" />
              </InputGroup.Text>
              <Form.Control
                placeholder="Search by device, site or location…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="border-start-0 ps-0"
              />
            </InputGroup>

            <Form.Select
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              style={{ width: 'auto' }}
              className="shadow-sm"
            >
              {ROWS_PER_PAGE_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n} / page
                </option>
              ))}
            </Form.Select>
          </div>

          {error && <div className="alert alert-danger">{error}</div>}

          {/* ---------- TABLE ---------- */}
          <div className="table-responsive position-relative">
            {loading && (
              <div className="text-center py-2">
                <div className="spinner-border spinner-border-sm text-primary" role="status" />
                <span className="ms-2 text-muted">Loading devices…</span>
              </div>
            )}
            <Table
              hover
              responsive="xl"
              className="device-table table-striped align-middle mb-0"
            >
              <thead className="table-light sticky-top">
                <tr>
                  <th>Device</th>
                  <th>Site</th>
                  <th className="d-none d-md-table-cell">Supervisors</th>
                  <th className="text-center">Action</th>
                </tr>
              </thead>

              <tbody>
                {devices.length === 0 && !loading ? (
                  <tr>
                    <td colSpan={4} className="text-center py-5 text-secondary">
                      No devices found
                    </td>
                  </tr>
                ) : (
                  devices.map((d) => (
                    <tr key={d._id || d.deviceId}>
                      {/* Device */}
                      <td className="fw-semibold">{d.deviceName}</td>

                      {/* Site */}
                      <td>{d.site_detail}</td>

                      {/* Supervisors — hidden on phones to keep the table fitting the viewport */}
                      <td className="d-none d-md-table-cell">
                        {(d.supervisorNames ?? []).join(', ') || '—'}
                      </td>

                      {/* Action */}
                      <td className="text-center">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => navigate(`/control-panel/${d.deviceId}`)}
                        >
                          <FaEye className="me-1" /> View
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>

          {/* ---------- PAGINATION ---------- */}
          <div className="d-flex flex-wrap gap-2 justify-content-between align-items-center mt-4 pt-3 border-top">
            <small className="text-muted">
              Showing{' '}
              <strong className="text-primary">
                {rangeStart}–{rangeEnd}
              </strong>{' '}
              of <strong className="text-primary">{totalCount}</strong>
            </small>

            <div className="d-flex align-items-center gap-2">
              <Button
                variant="outline-primary"
                disabled={currentPage === 1 || loading}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              >
                ← Prev
              </Button>
              <span className="text-muted small px-1">
                Page <strong className="text-primary">{currentPage}</strong> of{' '}
                <strong className="text-primary">{safeTotalPages}</strong>
              </span>
              <Button
                variant="outline-primary"
                disabled={currentPage >= safeTotalPages || loading}
                onClick={() => setCurrentPage((p) => Math.min(safeTotalPages, p + 1))}
              >
                Next →
              </Button>
            </div>
          </div>
        </Card.Body>
      </Card>

      <ToastContainer position="bottom-right" />
    </Container>
  );
};

export default ManageDevice;
